// ---------------------------------------------------------------------------
// The browser side of the NOTIFICATION ENGINE. V25.
//
// NO VOCABULARY IS SPELLED IN THIS FILE. A channel arrives as an id AND its
// word; a delivery state arrives as an id AND its word; a suppression reason
// arrives with the whole sentence a person reads. Absolute rule 4, and the trap
// `HANDOFF.md` §6 records four times now — where a client-facing word collided
// with an internal status because somebody typed it in TypeScript rather than
// reading it from the database.
//
// THE PREFERENCE ENDPOINTS TAKE NO PERSON. There is no `userId` parameter on
// anything here and there is no endpoint that would accept one: the server reads
// the account from the session, which is what makes "can this be used to find out
// whether somebody exists" a question with no shape.
// ---------------------------------------------------------------------------

import { mutate, read } from "./console";

/** A quiet window is a PAIR or it is nothing. There is no third shape. */
export type QuietHours = { from: string; to: string; time_zone: string };

export type NotificationPreference = {
  kind: string;
  kind_label: string;
  channel: string;
  channel_label: string;
  /** Whether the product can deliver on this channel at all today. */
  channel_available: boolean;
  enabled: boolean;
  /** Whether this is a choice the person made, or the default nobody asked about. */
  is_default: boolean;
};

export type NotificationSettings = {
  quiet_window: QuietHours | null;
  time_zone: string | null;
  preferences: NotificationPreference[];
};

export type NotificationDelivery = {
  delivery_id: string;
  intent_id: string;
  kind: string;
  kind_label: string;
  channel: string;
  channel_label: string;
  state: string;
  state_label: string;
  /**
   * THE COLOUR COMES FROM THE DATABASE, NOT FROM A `case` HERE.
   *
   * `0072`: the tone is a column on `app.notification_delivery_state`, so a state
   * added next slice arrives with its colour rather than falling through to
   * whichever branch a screen happens to end on. `11-…` §3 fixes the four names.
   */
  state_tone: string;
  reason: string | null;
  reason_label: string | null;
  reason_sentence: string | null;
  event_id: string | null;
  event_name: string | null;
  deliver_after: string | null;
  created_at: string;
  is_current: boolean;
};

/** COUNTS AND NOTHING ELSE. The server has no field here that names a person. */
export type DrainResult = {
  considered: number;
  queued: number;
  deferred: number;
  suppressed: number;
  sent: number;
  undeliverable: number;
};

export const notifications = {
  settings: (base: string) => read(base, "/v1/notifications/settings"),

  updateSettings: (
    base: string,
    payload: {
      preferences?: { kind: string; channel: string; enabled: boolean }[];
      /** Present and null CLEARS the window; absent leaves it alone. */
      quiet_window?: { from: string; to: string; time_zone?: string } | null;
    },
  ) => mutate("PATCH", base, "/v1/notifications/settings", payload),

  ledger: (base: string, limit = 100) =>
    read(base, `/v1/notifications/ledger?limit=${encodeURIComponent(String(limit))}`),

  drain: (base: string) => mutate("POST", base, "/v1/notifications/drain", {}),
};
